import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import 'moment/locale/th';
import shrinkImage from './shrinkImage';
import { setUrlParams } from './cleanerToken';

// หน้าผู้ดูแลเงิน (cleaners.can_manage_deposit) คืนเงินประกัน -- /once/cleaner?view=deposit-manage[&d=<id>]
// การ์ดในกลุ่มไลน์ลิงก์มาที่ &d= ตรงๆ (ไม่มี t -> ใช้รหัสที่จำไว้ ดู cleanerToken.js)
const money = (n) => Number(n || 0).toLocaleString('th-TH', { maximumFractionDigits: 2 });
const checkoutOf = (b) => (b?.end_date ? moment(b.end_date).add(1, 'day') : null); // end_date = คืนสุดท้าย
const errOf = (e) => e?.response?.data?.message || 'ไม่สำเร็จ — สัญญาณเน็ตอาจไม่ดี ลองอีกครั้ง';

// รายชื่อธนาคารเดียวกับหน้าลูกค้า (Booking/DepositRefund.js) -- ก๊อปมาไม่ import เพื่อไม่ลาก axios/i18n ของหน้านั้นเข้ามา
const BANKS = [
  'ธนาคารกสิกรไทย', 'ธนาคารไทยพาณิชย์', 'ธนาคารกรุงเทพ', 'ธนาคารกรุงไทย', 'ธนาคารกรุงศรีอยุธยา',
  'ธนาคารทหารไทยธนชาต (ttb)', 'ธนาคารออมสิน', 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร (ธ.ก.ส.)',
  'ธนาคารอาคารสงเคราะห์', 'ธนาคารยูโอบี', 'ธนาคารซีไอเอ็มบี ไทย', 'ธนาคารเกียรตินาคินภัทร', 'ธนาคารแลนด์ แอนด์ เฮ้าส์',
];
const OTHER_BANK = '__other__';

const copyText = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; }
  } catch (e) { /* fallback */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) { return false; }
};

// custom = รายการอื่นๆ (ไม่มีในแคตตาล็อก) -> แก้ชื่อได้ ส่ง { name, qty, unit_price }
const toEditRows = (expenses) => (expenses || []).map((e) => ({
  expense_id: e.expense_id, name: e.name || '', custom: !!e.custom || !e.expense_id, qty: Number(e.qty) || 1, unit_price: String(Number(e.unit_price) || 0),
}));

export default function DepositManage({ cw, S, onBack }) {
  const initialId = new URLSearchParams(window.location.search).get('d');
  const [tab, setTab] = useState('to_refund');
  const [list, setList] = useState(null);
  const [listError, setListError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [rows, setRows] = useState([]);        // รายการหัก (แก้ไขได้)
  const [dirty, setDirty] = useState(false);
  const [options, setOptions] = useState([]);
  const [slip, setSlip] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [needConfirm, setNeedConfirm] = useState(false); // 409 ยังไม่ได้ตรวจห้อง
  const [copied, setCopied] = useState(false);
  const [bankForm, setBankForm] = useState(null); // null = ปิด · { bank, other, no, name } = กำลังกรอกบัญชีแทนลูกค้า
  const [bankMsg, setBankMsg] = useState(null);   // { ok, text } ผลบันทึกบัญชี/ส่ง SMS

  const loadList = useCallback((t) => {
    setList(null); setListError(null);
    cw(`deposit-manage?tab=${t}`).then((r) => setList(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { setList([]); setListError(errOf(e)); });
  }, [cw]);

  const applyDetail = (d) => { setDetail(d); setRows(toEditRows(d.expenses)); setDirty(false); };

  const openDetail = useCallback((id) => {
    setDetailError(null); setSlip(null); setNeedConfirm(false); setCopied(false); setBankForm(null); setBankMsg(null);
    cw(`deposits/${id}`).then((r) => { applyDetail(r.data); setUrlParams({ d: id }); })
      .catch((e) => { setDetailError(errOf(e)); setUrlParams({ d: null }); });
  }, [cw]);

  useEffect(() => {
    moment.locale('th');
    if (initialId) openDetail(initialId); else loadList('to_refund');
    cw('deposits/expense-options').then((r) => setOptions(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const switchTab = (t) => { setTab(t); loadList(t); };
  const backToList = () => { setDetail(null); setDetailError(null); setUrlParams({ d: null }); loadList(tab); };

  // ---------- รายการหัก ----------
  const editRow = (i, patch) => { setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r))); setDirty(true); };
  const removeRow = (i) => { setRows((rs) => rs.filter((_, k) => k !== i)); setDirty(true); };
  const addRow = (id) => {
    const o = options.find((x) => String(x.id) === String(id));
    if (!o) return;
    setRows((rs) => [...rs, { expense_id: o.id, name: o.name, custom: false, qty: 1, unit_price: String(Number(o.default_price) || 0) }]);
    setDirty(true);
  };
  const addCustomRow = () => { setRows((rs) => [...rs, { expense_id: null, name: '', custom: true, qty: 1, unit_price: '' }]); setDirty(true); };
  const rowsTotal = rows.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0);
  const saveRows = () => {
    if (rows.some((r) => r.custom && !r.name.trim())) { alert('รายการอื่นๆ: กรุณาใส่ชื่อรายการ'); return; }
    if (rows.some((r) => r.custom && !(Number(r.unit_price) > 0))) { alert('รายการอื่นๆ: กรุณาใส่ยอดที่หัก (มากกว่า 0 บาท)'); return; }
    if (rows.some((r) => !(Number(r.qty) > 0) || !(Number(r.unit_price) >= 0) || r.unit_price === '')) { alert('จำนวน/ราคา ไม่ถูกต้อง'); return; }
    const items = rows.map((r) => (r.custom
      ? { name: r.name.trim(), qty: Number(r.qty), unit_price: Number(r.unit_price) }
      : { expense_id: r.expense_id, qty: Number(r.qty), unit_price: Number(r.unit_price) }));
    setBusy(true);
    cw(`deposit-manage/${detail.id}/expenses`, { method: 'put', data: { items } })
      .then(() => { setBusy(false); openDetail(detail.id); })
      .catch((e) => { setBusy(false); alert(errOf(e)); });
  };

  // ---------- คืนเงิน ----------
  const onPickSlip = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const small = await shrinkImage(file);
      const fd = new FormData(); fd.append('file', small, 'refund-slip.jpg');
      const r = await cw('upload-photo', { method: 'post', data: fd, timeout: 90000 });
      setSlip(r.data.url);
    } catch (e) { alert('ส่งรูปไม่สำเร็จ — ลองใหม่อีกครั้ง'); }
    setUploading(false);
  };
  const submitRefund = (confirmUninspected = false) => {
    if (dirty) { alert('บันทึกรายการหักก่อน แล้วค่อยคืนเงิน'); return; }
    if (!slip) { alert('กรุณาแนบสลิปการโอนก่อน'); return; }
    setBusy(true);
    cw(`deposit-manage/${detail.id}/refund`, { method: 'post', data: { slip_url: slip, ...(confirmUninspected ? { confirm_uninspected: true } : {}) } })
      .then(() => { setBusy(false); setSlip(null); setNeedConfirm(false); openDetail(detail.id); })
      .catch((e) => {
        setBusy(false);
        if (e?.response?.status === 409 && !confirmUninspected && /ยังไม่ได้ตรวจห้อง/.test(e?.response?.data?.message || '')) { setNeedConfirm(true); return; }
        alert(errOf(e));
      });
  };

  const onCopy = async () => {
    const ok = await copyText(String(detail.customer_bank_account || '').replace(/[^0-9]/g, ''));
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000); } else alert('คัดลอกไม่ได้ — กดค้างที่เลขบัญชีเพื่อคัดลอกเอง');
  };

  // ---------- บัญชีลูกค้า (หัวหน้ากรอกแทน / ส่ง SMS ซ้ำ) ----------
  const openBankForm = () => {
    const known = detail.customer_bank_name || '';
    const isOther = known && !BANKS.includes(known);
    setBankForm({ bank: isOther ? OTHER_BANK : known, other: isOther ? known : '', no: detail.customer_bank_account || '', name: detail.customer_bank_account_name || '' });
    setBankMsg(null);
  };
  const editBank = (patch) => setBankForm((f) => ({ ...f, ...patch }));
  const saveBank = () => {
    const bankName = (bankForm.bank === OTHER_BANK ? bankForm.other : bankForm.bank).trim();
    const digits = bankForm.no.replace(/[\s-]/g, '');
    if (!bankName) { setBankMsg({ ok: false, text: 'กรุณาเลือกธนาคาร' }); return; }
    if (!/^\d{6,20}$/.test(digits)) { setBankMsg({ ok: false, text: 'เลขบัญชีต้องเป็นตัวเลข 6–20 หลัก' }); return; }
    if (!bankForm.name.trim()) { setBankMsg({ ok: false, text: 'กรุณาใส่ชื่อบัญชี' }); return; }
    setBusy(true);
    cw(`deposit-manage/${detail.id}/bank`, { method: 'put', data: { bank_name: bankName, account_no: bankForm.no.trim(), account_name: bankForm.name.trim() } })
      .then((r) => { setBusy(false); applyDetail(r.data); setBankForm(null); setBankMsg({ ok: true, text: '✓ บันทึกบัญชีแล้ว' }); })
      .catch((e) => { setBusy(false); setBankMsg({ ok: false, text: errOf(e) }); });
  };
  const sendBankLink = () => {
    setBusy(true); setBankMsg(null);
    cw(`deposit-manage/${detail.id}/send-bank-link`, { method: 'post' })
      .then((r) => { setBusy(false); applyDetail(r.data); setBankMsg({ ok: true, text: `✓ ส่งแล้ว เวลา ${moment(r.data?.bank_link_sent_at || undefined).format('HH:mm')}` }); })
      .catch((e) => { setBusy(false); setBankMsg({ ok: false, text: errOf(e) }); });
  };

  const input = { boxSizing: 'border-box', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 8, fontSize: 16 };

  // ---------- DETAIL ----------
  if (detail || detailError) {
    if (detailError) {
      return (
        <div style={S.page}>
          <div style={S.bar}><button style={S.back} onClick={backToList}>‹ รายการ</button></div>
          <div style={{ ...S.card, textAlign: 'center', color: '#CD3E33' }}>{detailError}</div>
        </div>
      );
    }
    const d = detail;
    const refunded = d.state === 'refunded';
    const canAct = d.state === 'customer_paid';
    const co = checkoutOf(d.booking);
    const refund = dirty ? Number(d.deposit_amount || 0) - rowsTotal : Number(d.refund_amount ?? (Number(d.deposit_amount || 0) - Number(d.expenses_total_price || 0)));
    const damaged = d.inspection_result === 'damaged';
    const canEditBank = !refunded && d.state !== 'canceled';
    const bankFormView = bankForm && (
      <div style={{ marginTop: 10, padding: 12, borderRadius: 12, background: '#F6F8F6', display: 'grid', gap: 8 }}>
        <select value={bankForm.bank} onChange={(e) => editBank({ bank: e.target.value })} style={{ ...input, width: '100%', background: '#fff' }}>
          <option value="">เลือกธนาคาร…</option>
          {BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
          <option value={OTHER_BANK}>อื่นๆ</option>
        </select>
        {bankForm.bank === OTHER_BANK && <input value={bankForm.other} onChange={(e) => editBank({ other: e.target.value })} placeholder="ชื่อธนาคาร" style={{ ...input, width: '100%' }} />}
        <input value={bankForm.no} onChange={(e) => editBank({ no: e.target.value })} inputMode="numeric" autoComplete="off" placeholder="เลขบัญชี" style={{ ...input, width: '100%', letterSpacing: 1 }} />
        <input value={bankForm.name} onChange={(e) => editBank({ name: e.target.value })} placeholder="ชื่อบัญชี" style={{ ...input, width: '100%' }} />
        <button disabled={busy} style={{ ...S.bigBtn('#2C6DAF'), padding: 12, fontSize: 17, marginTop: 0 }} onClick={saveBank}>{busy ? 'กำลังบันทึก…' : 'บันทึก'}</button>
        <button style={{ ...S.back, width: '100%', color: '#586159' }} onClick={() => { setBankForm(null); setBankMsg(null); }}>ยกเลิก</button>
      </div>
    );
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={backToList}>‹ รายการ</button>
          <div><h1 style={{ ...S.h, fontSize: 17 }}>{d.room_name} · #{d.booking?.booking_no}</h1><div style={S.sub}>{d.booking?.contact_name}{co ? ` · ออก ${co.format('D MMM')}` : ''}</div></div>
        </div>

        {/* ยอด */}
        <div style={{ ...S.card, background: refunded ? '#F1F7F3' : '#fff' }}>
          <div style={{ display: 'flex', fontSize: 15 }}><div style={{ flex: 1 }}>เงินประกัน</div><b>฿{money(d.deposit_amount)}</b></div>
          <div style={{ display: 'flex', fontSize: 15, marginTop: 4 }}><div style={{ flex: 1 }}>หักค่าเสียหาย</div><b>-฿{money(dirty ? rowsTotal : d.expenses_total_price)}</b></div>
          <div style={{ display: 'flex', alignItems: 'baseline', marginTop: 8, paddingTop: 8, borderTop: '1px solid #EEF1EE' }}>
            <div style={{ flex: 1, fontWeight: 800 }}>{refunded ? 'คืนแล้ว' : 'ต้องคืนลูกค้า'}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: refund < 0 ? '#CD3E33' : '#0F8A56' }}>฿{money(refund)}</div>
          </div>
          {refund < 0 && <div style={{ marginTop: 6, padding: 10, borderRadius: 10, background: '#FDECEA', color: '#CD3E33', fontSize: 14, fontWeight: 700 }}>⚠️ ค่าเสียหายเกินเงินประกัน — คืนผ่านระบบไม่ได้ ต้องคุยกับลูกค้าเรื่องส่วนที่เกิน</div>}
          {refunded && d.refunded_time && <div style={{ ...S.sub, marginTop: 6 }}>💸 โอนคืนเมื่อ {moment(d.refunded_time).format('D MMM YYYY HH:mm')}</div>}
          {refunded && d.refunded_slip && <a href={d.refunded_slip} target="_blank" rel="noreferrer"><img src={d.refunded_slip} alt="สลิปคืนเงิน" style={{ marginTop: 8, maxHeight: 200, maxWidth: '100%', borderRadius: 8 }} /></a>}
        </div>

        {/* ผลตรวจห้อง */}
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, fontWeight: 800 }}>ตรวจห้อง</div>
            {!d.inspected_at && <span style={S.pill('#B26B0E')}>ยังไม่ตรวจ</span>}
            {d.inspected_at && <span style={S.pill(damaged ? '#CD3E33' : '#0F8A56')}>{damaged ? '⚠️ เสียหาย' : '✅ ห้องปกติ'}</span>}
          </div>
          {d.inspected_at && <div style={{ ...S.sub, marginTop: 4 }}>ตรวจเมื่อ {moment(d.inspected_at).format('D MMM HH:mm')}</div>}
          {d.inspection_note && <div style={{ fontSize: 15, marginTop: 6, whiteSpace: 'pre-wrap' }}>{d.inspection_note}</div>}
          {(d.inspection_photos || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {d.inspection_photos.map((u) => <a key={u} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" style={{ height: 80, width: 80, objectFit: 'cover', borderRadius: 8 }} /></a>)}
            </div>
          )}
        </div>

        {/* รายการหัก */}
        <div style={S.card}>
          <div style={{ fontWeight: 800, marginBottom: 4 }}>รายการหัก</div>
          {rows.length === 0 && <div style={{ ...S.sub, padding: '6px 0' }}>ไม่มี</div>}
          {rows.map((r, i) => (
            <div key={`${r.expense_id || 'c'}-${i}`} style={{ padding: '8px 0', borderTop: '1px solid #EEF1EE' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {canAct && r.custom
                  ? <input value={r.name} onChange={(e) => editRow(i, { name: e.target.value })} placeholder="ชื่อรายการ (อื่นๆ)" style={{ ...input, width: 0, flex: 1 }} />
                  : <div style={{ flex: 1, fontWeight: 700 }}>{r.name || '-'}{r.custom && <span style={{ fontSize: 12, color: '#8A938B', fontWeight: 600 }}> (อื่นๆ)</span>}</div>}
                {canAct
                  ? <button style={{ ...S.back, color: '#CD3E33', padding: '4px 10px' }} onClick={() => removeRow(i)}>ลบ</button>
                  : <b>฿{money((Number(r.qty) || 0) * (Number(r.unit_price) || 0))}</b>}
              </div>
              {canAct ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  <button style={{ ...S.back, fontSize: 18, padding: '2px 12px' }} onClick={() => editRow(i, { qty: Math.max(1, Number(r.qty) - 1) })}>−</button>
                  <div style={{ minWidth: 22, textAlign: 'center', fontWeight: 800 }}>{r.qty}</div>
                  <button style={{ ...S.back, fontSize: 18, padding: '2px 12px' }} onClick={() => editRow(i, { qty: Number(r.qty) + 1 })}>+</button>
                  <span style={{ marginLeft: 6, fontSize: 13, color: '#586159' }}>× ฿</span>
                  <input type="number" inputMode="decimal" value={r.unit_price} onChange={(e) => editRow(i, { unit_price: e.target.value })} style={{ ...input, width: 0, flex: 1, minWidth: 70 }} />
                </div>
              ) : <div style={{ fontSize: 12, color: '#8A938B' }}>{r.qty} × ฿{money(r.unit_price)}</div>}
            </div>
          ))}
          {canAct && (
            <button style={{ ...S.back, width: '100%', marginTop: 8, padding: 10, color: '#2C6DAF' }} onClick={addCustomRow}>+ อื่นๆ (ไม่มีในรายการ)</button>
          )}
          {canAct && options.length > 0 && (
            <select value="" onChange={(e) => addRow(e.target.value)} style={{ ...input, width: '100%', marginTop: 8, background: '#fff', color: '#2C6DAF', fontWeight: 700 }}>
              <option value="">+ เพิ่มรายการหัก…</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.name} (฿{money(o.default_price)})</option>)}
            </select>
          )}
          {canAct && dirty && (
            <button disabled={busy} style={{ ...S.bigBtn('#2C6DAF'), padding: 12, fontSize: 17 }} onClick={saveRows}>{busy ? 'กำลังบันทึก…' : `บันทึกรายการหัก (รวม ฿${money(rowsTotal)})`}</button>
          )}
        </div>

        {/* บัญชีลูกค้า */}
        <div style={S.card}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>บัญชีรับเงินคืนของลูกค้า</div>
          {d.customer_bank_account ? (
            <>
              <div style={{ fontSize: 15 }}>{d.customer_bank_name}</div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1, userSelect: 'all' }}>{d.customer_bank_account}</div>
              <div style={{ fontSize: 15 }}>{d.customer_bank_account_name}</div>
              {d.bank_entered_by_cleaner_id && <div style={{ ...S.sub, marginTop: 4 }}>หัวหน้ากรอกแทนลูกค้า</div>}
              {!refunded && (
                <button style={{ ...S.back, width: '100%', marginTop: 10, padding: 12, color: copied ? '#0F8A56' : '#2C6DAF' }} onClick={onCopy}>
                  {copied ? '✓ คัดลอกแล้ว' : '📋 คัดลอกเลขบัญชี'}
                </button>
              )}
              {canEditBank && !bankForm && (
                <button style={{ border: 'none', background: 'none', padding: '8px 0 0', fontSize: 14, color: '#2C6DAF', textDecoration: 'underline' }} onClick={openBankForm}>แก้ไขบัญชี</button>
              )}
            </>
          ) : (
            <>
              <div style={{ fontSize: 15, color: '#B26B0E', fontWeight: 700 }}>รอลูกค้ากรอกบัญชี (ส่ง SMS ให้แล้ว)</div>
              {d.contact_tel && <div style={{ fontSize: 15, marginTop: 4 }}>โทรลูกค้า: <a href={`tel:${d.contact_tel}`}>{d.contact_tel}</a></div>}
              {canEditBank && !bankForm && (
                <>
                  <button style={{ ...S.back, width: '100%', marginTop: 10, padding: 12, color: '#2C6DAF' }} onClick={openBankForm}>กรอกบัญชีแทนลูกค้า</button>
                  <button disabled={busy} style={{ ...S.back, width: '100%', marginTop: 8, padding: 12, color: '#2C6DAF' }} onClick={sendBankLink}>{busy ? 'กำลังส่ง…' : 'ส่ง SMS ลิงก์กรอกบัญชีอีกครั้ง'}</button>
                  {d.bank_link_sent_at && <div style={{ ...S.sub, marginTop: 4 }}>ส่งล่าสุด {moment(d.bank_link_sent_at).format('HH:mm')}</div>}
                </>
              )}
            </>
          )}
          {canEditBank && bankFormView}
          {canEditBank && bankMsg && <div style={{ marginTop: 8, fontSize: 14, fontWeight: 700, color: bankMsg.ok ? '#0F8A56' : '#CD3E33' }}>{bankMsg.text}</div>}
        </div>

        {/* โอนแล้ว · แนบสลิป */}
        {canAct && (
          <div style={S.card}>
            {slip
              ? <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={slip} alt="สลิป" style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 10 }} />
                  <button onClick={() => { setSlip(null); setNeedConfirm(false); }} style={{ position: 'absolute', top: 6, right: 6, width: 34, height: 34, borderRadius: 17, border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 16 }}>✕</button>
                </div>
              : <label style={{ display: 'block', border: '2px dashed #0F8A56', borderRadius: 12, padding: 20, fontSize: 18, fontWeight: 800, textAlign: 'center', color: '#0F8A56' }}>
                  {uploading ? '⏳ กำลังส่งรูป…' : '💸 โอนแล้ว · แนบสลิป'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { onPickSlip(e.target.files?.[0]); e.target.value = ''; }} />
                </label>}

            {slip && !needConfirm && (
              <button disabled={busy || uploading || refund < 0} style={S.bigBtn(refund < 0 ? '#8A938B' : '#0F8A56')} onClick={() => submitRefund(false)}>
                {busy ? 'กำลังส่ง…' : `ยืนยันคืนเงิน ฿${money(refund)}`}
              </button>
            )}
            {slip && needConfirm && (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: '#FFF4E5', border: '1.5px solid #B26B0E' }}>
                <div style={{ fontWeight: 800, color: '#B26B0E' }}>⚠️ ยังไม่ได้ตรวจห้อง</div>
                <div style={{ fontSize: 14, marginTop: 4 }}>แม่บ้านยังไม่ได้ส่งผลตรวจห้องนี้ ต้องการคืนเงินเลยหรือไม่?</div>
                <button disabled={busy} style={{ ...S.bigBtn('#B26B0E'), padding: 14, fontSize: 17 }} onClick={() => submitRefund(true)}>{busy ? 'กำลังส่ง…' : 'ยืนยัน คืนเงินโดยไม่ตรวจห้อง'}</button>
                <button style={{ ...S.back, width: '100%', marginTop: 8, color: '#586159' }} onClick={() => setNeedConfirm(false)}>ยกเลิก</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ---------- LIST ----------
  const tabBtn = (t, label) => (
    <button onClick={() => switchTab(t)} style={{ flex: 1, border: 'none', padding: '10px 0', fontSize: 16, fontWeight: 800, background: tab === t ? '#0F8A56' : 'transparent', color: tab === t ? '#fff' : '#8A938B' }}>{label}</button>
  );
  return (
    <div style={S.page}>
      <div style={S.bar}>
        <button style={S.back} onClick={onBack}>‹ หน้าหลัก</button>
        <div><h1 style={{ ...S.h, fontSize: 17 }}>คืนเงินประกัน</h1><div style={S.sub}>แตะรายการเพื่อดู/แนบสลิปคืนเงิน</div></div>
      </div>
      <div style={{ ...S.toggleWrap, marginBottom: 14 }}>{tabBtn('to_refund', 'รอคืน')}{tabBtn('done', 'คืนแล้ว')}</div>

      {list === null && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>กำลังโหลด…</div>}
      {listError && <div style={{ ...S.card, textAlign: 'center', color: '#CD3E33' }}>{listError}</div>}
      {list && !listError && list.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>{tab === 'done' ? 'ยังไม่มีรายการคืนเงินใน 30 วัน' : 'ไม่มีรายการรอคืน'}</div>}
      {(list || []).map((d) => {
        const co = checkoutOf(d.booking);
        const amount = d.refund_amount ?? (Number(d.deposit_amount || 0) - Number(d.expenses_total_price || 0));
        return (
          <button key={d.id} onClick={() => openDetail(d.id)} style={{ ...S.card, display: 'block', width: '100%', textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 800 }}>{d.room_name} · #{d.booking?.booking_no}</div>
                <div style={{ fontSize: 13, color: '#586159' }}>{d.booking?.contact_name}{co ? ` · ออก ${co.format('D MMM')}` : ''}</div>
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: Number(amount) < 0 ? '#CD3E33' : '#1A211D' }}>฿{money(amount)}</div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {tab === 'to_refund' && <span style={{ ...S.pill(d.inspected_at ? '#0F8A56' : '#B26B0E'), fontSize: 12 }}>{d.inspected_at ? 'ตรวจห้องแล้ว' : 'ยังไม่ตรวจ'}</span>}
              {tab === 'to_refund' && <span style={{ ...S.pill(d.has_bank ? '#2C6DAF' : '#8A938B'), fontSize: 12 }}>{d.has_bank ? 'มีบัญชี' : 'รอลูกค้ากรอกบัญชี'}</span>}
              {d.inspection_result === 'damaged' && <span style={{ ...S.pill('#CD3E33'), fontSize: 12 }}>เสียหาย</span>}
              {tab === 'done' && d.refunded_time && <span style={{ ...S.pill('#0F8A56'), fontSize: 12 }}>คืนแล้ว {moment(d.refunded_time).format('D MMM')}</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}
